import React from "react";
import {
  ClientProps,
  CustomerProps,
  FilterProps,
  InvoiceProps,
  ListComponentProps,
  UserProps,
  QuickbooksCompanyProps,
  StatusProps,
  TemplateProps,
  ProjectProps,
  TaskProps,
  ActivityProps,
  ContactProps,
  ToDoProps,
  ProjectTemplateProps,
  TagProps,
  GroupProps,
  FileProps,
  PaginationProps,
} from "../interfaces/interfaces";
import { PROJECT_INITIAL_STATE } from "../utils/data";

interface Props {
  dataLoading: boolean;
  dataSuccess: { status: boolean; message: string };
  dataError: { status: boolean; message: string };
  dataWarning: {
    status: boolean;
    message: string;
    actionLabel?: string;
    action?: () => void;
  };
  statusList: StatusProps[];
  invoiceList: InvoiceProps[];
  customerOptions: ListComponentProps[];
  isModalStatusOpen: boolean;
  statusToEdit: StatusProps | null;
  client: null | ClientProps;
  isTourOpen: boolean;
  userAccount: any;
  termOptions: ListComponentProps[];
  customerByClient: CustomerProps[];
  customerByClientPaginated: {
    data: CustomerProps[];
    pagination: PaginationProps;
  };
  projectInformation: ProjectProps;
  templateList: TemplateProps[];
  usersList: UserProps[];
  userOptions: { label: string; value: string }[];
  projectListPaginated: {
    data: ProjectProps[];
    pagination: PaginationProps;
  };
  projectList: ProjectProps[];
  projectTemplateList: ProjectTemplateProps[];
  taskList: {
    data: TaskProps[];
    pagination: PaginationProps;
  };
  taskDashboard: {
    data: TaskProps[];
    pagination: PaginationProps;
  };
  activityList: {
    data: ActivityProps[];
    pagination: PaginationProps;
  };
  contactList: ContactProps[];
  contactListPaginated: {
    data: ContactProps[];
    pagination: PaginationProps;
  };
  todoList: ToDoProps[];
  tagList: TagProps[];
  taskModal: {
    open: boolean;
    create: boolean;
    update: boolean;
    payload: TaskProps | null;
  } | null;
  projectModal: {
    open: boolean;
    create: boolean;
    update: boolean;
    selected: string[];
    payload: ProjectProps | null;
  } | null;
  activityModal: {
    open: boolean;
    create: boolean;
    update: boolean;
    payload: ActivityProps | null;
  } | null;
  contactModal: {
    open: boolean;
    create: boolean;
    update: boolean;
    payload: ContactProps | null;
  } | null;
  projectSelected: string | null;
  templateSelected: ProjectTemplateProps | null;
  projectsByTemplate: ProjectProps[];
  statusByTemplate: { name: string; _id: string }[];
  userGroups: GroupProps[];
  customerFiles: {
    data: FileProps[];
    pagination: PaginationProps;
  };
  downloadedFile: string;
  files: {
    data: FileProps[];
    pagination: PaginationProps;
  };
  handleChangeTemplateSelected: (value: ProjectTemplateProps | null) => void;
  handleChangeProjectSelected: (value: string | null) => void;
  handleTour: (status: boolean) => void;
  openCloseModalStatus: (value: boolean) => void;
  handleWarning: (
    status: boolean,
    message: string,
    actionLabel?: string,
    action?: () => void,
  ) => void;
  handleSuccess: (status: boolean, message: string) => void;
  selectStatusToEdit: (value: StatusProps | null) => void;
  resetContacts: () => void;
  getData: (
    endpoint:
      | "status"
      | "customer"
      | "invoice"
      | "deposit"
      | "credit"
      | "payment"
      | "journal"
      | "client"
      | "user-account"
      | "profile-info"
      | "term"
      | "templates"
      | "users"
      | "projects"
      | "projects-paginated"
      | "project-templates"
      | "activities-paginated"
      | "tasks"
      | "task-dashboard"
      | "contacts"
      | "contacts-paginated"
      | "todos"
      | "tags"
      | "groups"
      | "files",
    idClient: string,
    search?: string,
    filters?: any,
    showAlert?: boolean,
  ) => void;
  getDataById: ({
    type,
    endpoint,
    search,
    filters,
    showAlert,
  }: {
    type: "project-template" | "template-status" | "customer-files";
    idClient?: string;
    endpoint: string;
    search?: string;
    filters?: any;
    showAlert?: boolean;
  }) => void;
  getReportData: (
    endpoint: "report" | "logs",
    idClient: string,
    filters: FilterProps,
    next?: (response: any) => void,
  ) => void;
  handleCreateData: ({
    endpoint,
    idClient,
    data,
    next,
    nextOnFailed,
    showAlert,
  }: {
    endpoint: string;
    idClient: string;
    data: any;
    next?: (newData?: any) => void;
    nextOnFailed?: () => void;
    showAlert?: boolean;
  }) => void;
  handleEditData: ({
    endpoint,
    newData,
    next,
    nextOnFailed,
    showAlert,
  }: {
    endpoint: string;
    newData: any;
    next?: () => void;
    nextOnFailed?: () => void;
    showAlert?: boolean;
  }) => void;
  handleBulkEdit: ({
    endpoint,
    items,
    newData,
    next,
  }: {
    endpoint: string;
    items: string[];
    newData: any;
    next?: () => void;
  }) => void;
  handleDeleteData: ({
    endpoint,
    next,
  }: {
    endpoint: string;
    next: () => void;
  }) => void;
  handleBulkDeleteData: (
    endpoint:
      | "status"
      | "invoice"
      | "payment"
      | "deposit"
      | "credit"
      | "journal",
    idClient: string,
    idList: string[],
  ) => void;
  fetchUserAccounts: (idUser: string) => void;
  getQbCompanyInfo: (
    realmId: string,
    next: (data: QuickbooksCompanyProps) => void,
  ) => void;
  getCustomersListByClient: ({
    idClient,
    filters,
    search,
    next,
  }: {
    idClient: string;
    filters?: { customer: string[] | null; location?: string };
    search?: string | null;
    next?: () => void;
  }) => void;
  getCustomersListByClientPaginated: ({
    idClient,
    filters,
    search,
    next,
  }: {
    idClient: string;
    filters?: any;
    search?: string | null;
    next?: () => void;
  }) => void;
  getCustomersListByProject: ({
    idClient,
    idProject,
    filters,
    search,
    next,
  }: {
    idClient: string;
    idProject: string;
    filters?: { customer: string[] | null };
    search?: string | null;
    next?: () => void;
  }) => void;
  openItemModal: (
    type: "task" | "project" | "activity" | "contact",
    create: boolean,
    payload?: any,
    itemId?: string,
    selected?: string[],
  ) => void;
  closeItemModal: (type: "task" | "project" | "activity" | "contact") => void;
  handleChangeProjectModalStatus: (
    newState: {
      open: boolean;
      create: boolean;
      update: boolean;
      selected: string[];
      payload: ProjectProps | null;
    } | null,
  ) => void;
  createOrUpdateTask: (
    customerInfo: CustomerProps,
    values: TaskProps,
    idClient: string,
    idTask?: string,
    next?: () => void,
  ) => void;
  createOrUpdateActivity: (
    customerInfo: CustomerProps,
    values: ActivityProps,
    idClient: string,
    idActivity?: string,
  ) => void;
  createOrUpdateContact: (
    customerInfo: CustomerProps,
    values: ContactProps,
    idClient: string,
  ) => void;
  uploadFile: ({
    endpoint,
    formData,
    next,
    onError,
  }: {
    endpoint: string;
    formData: FormData;
    next?: (response: any) => void;
    onError?: () => void;
  }) => void;
  downloadFile: ({
    endpoint,
    next,
  }: {
    endpoint: string;
    next: () => void;
  }) => void;
  createFile: (
    data: { type: string; data: Buffer },
    name: string,
    download: boolean,
  ) => void;
}

const DataContext = React.createContext<Props>({
  dataLoading: false,
  dataSuccess: { status: false, message: "" },
  dataError: { status: false, message: "" },
  dataWarning: {
    status: false,
    message: "",
    actionLabel: "",
    action: () => {},
  },
  statusList: [],
  customerOptions: [],
  invoiceList: [],
  isModalStatusOpen: false,
  statusToEdit: null,
  client: null,
  isTourOpen: false,
  userAccount: [],
  termOptions: [],
  customerByClient: [],
  customerByClientPaginated: {
    data: [],
    pagination: { current: "1", totalPages: "1", total: "" },
  },
  projectInformation: PROJECT_INITIAL_STATE,
  templateList: [],
  usersList: [],
  userOptions: [],
  projectListPaginated: {
    data: [],
    pagination: { current: "1", totalPages: "1", total: "" },
  },
  contactListPaginated: {
    data: [],
    pagination: { current: "1", totalPages: "1", total: "" },
  },
  projectList: [],
  projectTemplateList: [],
  taskList: {
    data: [],
    pagination: { current: "1", totalPages: "1", total: "" },
  },
  taskDashboard: {
    data: [],
    pagination: { current: "1", totalPages: "1", total: "" },
  },
  activityList: {
    data: [],
    pagination: { current: "1", totalPages: "1", total: "" },
  },
  contactList: [],
  todoList: [],
  tagList: [],
  taskModal: null,
  projectModal: null,
  activityModal: null,
  contactModal: null,
  projectSelected: null,
  templateSelected: null,
  projectsByTemplate: [],
  statusByTemplate: [],
  userGroups: [],
  customerFiles: {
    data: [],
    pagination: { current: "1", totalPages: "1", total: "" },
  },
  downloadedFile: "",
  files: {
    data: [],
    pagination: { current: "1", totalPages: "1", total: "" },
  },
  handleChangeTemplateSelected: () => {},
  handleChangeProjectSelected: () => {},
  handleTour: () => {},
  openCloseModalStatus: () => {},
  handleWarning: () => {},
  selectStatusToEdit: () => {},
  resetContacts: () => {},
  getData: () => {},
  getDataById: () => {},
  getReportData: () => {},
  handleCreateData: () => {},
  handleEditData: () => {},
  handleBulkEdit: () => {},
  handleDeleteData: () => {},
  handleBulkDeleteData: () => {},
  handleSuccess: () => {},
  fetchUserAccounts: () => {},
  getQbCompanyInfo: () => {},
  getCustomersListByClient: () => {},
  getCustomersListByClientPaginated: () => {},
  getCustomersListByProject: () => {},
  openItemModal: () => {},
  closeItemModal: () => {},
  handleChangeProjectModalStatus: () => {},
  createOrUpdateTask: () => {},
  createOrUpdateActivity: () => {},
  createOrUpdateContact: () => {},
  uploadFile: () => {},
  downloadFile: () => {},
  createFile: () => {},
});

export default DataContext;
